/**
 * Command K — the provider contract.
 *
 * One provider per result kind. `CmdkSearchService` holds the registry, calls
 * every enabled provider, merges and ranks. A kind can therefore be added,
 * removed or capability-gated as ONE file — and an AI-off tenant simply drops
 * the `ask` provider rather than threading a flag through the render tree.
 *
 * AI CONTEXT
 *
 * Providers come in two flavours and the difference is the whole performance
 * story:
 *
 *  - **Local** (actions, navigation, apps, widgets): synchronous, no network,
 *    complete catalogue in memory. `search()` returns an array. These render
 *    on the first keystroke.
 *  - **Remote** (spaces, people, news, docs): asynchronous, debounced,
 *    cancellable, and gated behind `minQueryLength`. `search()` returns an
 *    `ApiResult` so a failure surfaces as a per-group error banner instead of
 *    silently emptying the list.
 *
 * A provider NEVER navigates, NEVER mutates and NEVER touches the AI. It
 * answers one question: what rows exist for this query?
 */

import type { ApiResult } from '../../../../../core/models/api-result';
import type { ApiState } from '../../../../../core/models/api-state';
import type { CmdkItem, CmdkKind, CmdkScope } from './cmdk.model';

/** What a provider is told about the moment it is being asked. */
export interface CmdkProviderContext {
  /** The query with any prefix already stripped, trimmed. */
  readonly query: string;
  /** The active scope chip. */
  readonly scope: CmdkScope;
  /** Where the user is — the palette's context-boost input. */
  readonly section: string;
  /** The focused building, when the surface has one. Several roots require it. */
  readonly locationId: string | null;
  /** The focused floor, when the surface has one. */
  readonly floorId: string | null;
  /** Hierarchical action strings the viewer holds. */
  readonly userActions: readonly string[];
  /** Aborts when the query moves on. Remote providers must honour it. */
  readonly signal: AbortSignal;
}

export interface CmdkProvider {
  /** Stable id, used for the per-group state map and for logging. */
  readonly id: string;
  readonly kind: CmdkKind;
  /** The scope chip this provider answers to, besides `all`. */
  readonly scope: CmdkScope;

  /**
   * Shortest query this provider will act on. Local providers use 0; remote
   * ones use 2, so a single character never fans out to the network.
   */
  readonly minQueryLength: number;

  /**
   * What this provider does on an empty query under the `all` chip — the
   * palette's idle state.
   *
   *  - `browse` — contribute its catalogue. Right for cheap local sources
   *    that make the idle palette useful (suggested actions, recents).
   *  - `hide`  — contribute nothing. Right for anything remote (a query per
   *    ⌘K before the user has typed is a query for nothing), and for
   *    catalogues large enough to bury the suggestions (every destination,
   *    every widget, every app).
   *
   * Selecting the provider's OWN chip always browses, whatever this says —
   * that is what a chip is for.
   */
  readonly idle: 'browse' | 'hide';

  /**
   * False when the viewer's capabilities or actions exclude this kind
   * entirely. Checked before every call — a disabled provider produces no
   * rows, no header, no skeleton and no error.
   *
   * Given the context being searched, so a gate on the viewer's actions reads
   * `ctx.userActions` — the same actions the search itself runs with.
   */
  isEnabled(ctx?: CmdkProviderContext): boolean;

  /**
   * Everything, for an empty query under this provider's scope chip. Browsing
   * a chip with no query is a first-class mode, not an empty state.
   */
  browse(ctx: CmdkProviderContext): CmdkItem[] | Promise<ApiResult<CmdkItem[]>>;

  /** Matches for a query. */
  search(ctx: CmdkProviderContext): CmdkItem[] | Promise<ApiResult<CmdkItem[]>>;

  /**
   * Exposed for remote providers so the results pane can render a skeleton
   * while in flight and an error banner on failure — per group, so one dead
   * backend never blanks the whole palette.
   */
  readonly state?: ApiState<CmdkItem[]>;
}

/**
 * A provider that goes to the network.
 *
 * The split matters structurally, not just descriptively. `CmdkSearchService`
 * gathers LOCAL providers inside a `computed()`; a remote provider in that
 * loop would fire an undebounced, uncancellable request from inside a reactive
 * read, once per recompute. So remote providers are never called from a
 * computed — only from the scheduler effect — and they are typed differently
 * so that cannot be done by accident.
 *
 * The division of labour:
 *   - the SERVICE owns scheduling: who runs, when, and with which generation;
 *   - the PROVIDER owns its own state writes, and the staleness check that
 *     guards them.
 *
 * That keeps "add a kind = one file" true, and puts the discard check next to
 * the fetch it belongs to rather than three layers away.
 */
export interface CmdkRemoteProvider extends CmdkProvider {
  /** Always present here — the results pane needs it for skeletons/errors. */
  readonly state: ApiState<CmdkItem[]>;

  /**
   * Fetch for this context and write the outcome into `state`.
   *
   * MUST check `ctx.signal.aborted` immediately after awaiting and return
   * without touching state if it is set. Note that this is a DISCARD token,
   * not cancellation: `GraphQLClientService.queryResult()` takes no signal and
   * the Apollo promise cannot be cancelled, so the request still completes and
   * we throw the answer away. Nothing here closes a socket.
   */
  load(ctx: CmdkProviderContext): Promise<void>;

  /**
   * Mark a fetch as imminent. Called by the scheduler the moment it decides
   * this provider will run — BEFORE the debounce, so the group's skeleton
   * appears while the user is still typing rather than popping in when the
   * request finally leaves.
   */
  markLoading(): void;

  /**
   * Clear back to empty because this provider should not be running — the
   * scope moved away, the query fell below the floor, or the palette closed.
   * Distinct from an empty RESULT, which is a legitimate answer and keeps its
   * group header.
   */
  standDown(): void;
}

/** True when a provider goes to the network. */
export function isRemoteProvider(provider: CmdkProvider): provider is CmdkRemoteProvider {
  return typeof (provider as CmdkRemoteProvider).load === 'function';
}

/** True when a provider's result needs awaiting. */
export function isAsyncResult(
  value: CmdkItem[] | Promise<ApiResult<CmdkItem[]>>,
): value is Promise<ApiResult<CmdkItem[]>> {
  return typeof (value as Promise<unknown>)?.then === 'function';
}

/**
 * Should this provider run at all, for this context? One predicate, so the
 * "why is there no header?" answer is always in one place.
 */
export function shouldRunProvider(provider: CmdkProvider, ctx: CmdkProviderContext): boolean {
  if (!provider.isEnabled(ctx)) return false;
  if (ctx.scope !== 'all' && ctx.scope !== provider.scope) return false;

  if (ctx.query.length === 0) {
    // Selecting a chip is explicit intent to browse that catalogue, so the
    // length floor does not apply. Under `all`, only the providers that opted
    // into the idle state contribute.
    if (ctx.scope === provider.scope) return true;
    return provider.idle === 'browse';
  }

  return ctx.query.length >= provider.minQueryLength;
}
